/**
 * PublishWizard Component
 * Multi-step wizard for publishing strategies to marketplace
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  createDraft,
  updateDraft,
  validateDraft,
  submitDraft,
  CATEGORIES,
  TIMEFRAMES,
  PRICING_MODELS,
  type DraftStrategy,
  type CreateDraftPayload,
  type UpdateDraftPayload,
} from '../services/marketplaceApi'

interface PublishWizardProps {
  isOpen: boolean
  onClose: () => void
  editDraft?: DraftStrategy | null
  onSuccess?: (strategyId: string) => void
}

// Demo user - in production, this would come from auth context
const DEMO_USER_ID = 'user_demo'
const DEMO_USER_NAME = 'Demo Trader'

const STEPS = [
  { id: 1, title: 'Basic Info', icon: '📝' },
  { id: 2, title: 'Strategy Config', icon: '⚙️' },
  { id: 3, title: 'Pricing', icon: '💰' },
  { id: 4, title: 'Review', icon: '✅' },
]

export default function PublishWizard({ 
  isOpen, 
  onClose, 
  editDraft,
  onSuccess 
}: PublishWizardProps) {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])
  
  // Form state
  const [formData, setFormData] = useState<CreateDraftPayload>({
    name: '',
    description: '',
    creator_id: DEMO_USER_ID,
    creator_name: DEMO_USER_NAME,
    category: 'trend_following',
    timeframe: 'intraday',
    indicators: [],
    exchanges: ['NSE', 'NFO'],
    instruments: ['NIFTY', 'BANKNIFTY'],
    pricing: 'free',
    price: 0,
    tags: [],
  })
  
  const [indicatorInput, setIndicatorInput] = useState('')
  const [tagInput, setTagInput] = useState('')

  // Reset form when draft changes
  useEffect(() => {
    if (editDraft) {
      setFormData({
        name: editDraft.name,
        description: editDraft.description,
        creator_id: editDraft.creator_id,
        creator_name: editDraft.creator_name,
        category: editDraft.category,
        timeframe: editDraft.timeframe,
        indicators: editDraft.indicators,
        exchanges: editDraft.exchanges,
        instruments: editDraft.instruments,
        pricing: editDraft.pricing,
        price: editDraft.price,
        tags: editDraft.tags,
      })
      setCurrentStep(1)
    } else {
      setFormData({
        name: '',
        description: '',
        creator_id: DEMO_USER_ID,
        creator_name: DEMO_USER_NAME,
        category: 'trend_following',
        timeframe: 'intraday',
        indicators: [],
        exchanges: ['NSE', 'NFO'],
        instruments: ['NIFTY', 'BANKNIFTY'],
        pricing: 'free',
        price: 0,
        tags: [],
      })
      setCurrentStep(1)
    }
    setErrors([])
  }, [editDraft, isOpen])

  if (!isOpen) return null

  const handleAddIndicator = () => {
    if (indicatorInput.trim() && !formData.indicators?.includes(indicatorInput.trim())) {
      setFormData({
        ...formData,
        indicators: [...(formData.indicators || []), indicatorInput.trim()]
      })
      setIndicatorInput('')
    }
  }

  const handleRemoveIndicator = (ind: string) => {
    setFormData({
      ...formData,
      indicators: formData.indicators?.filter(i => i !== ind) || []
    })
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()]
      })
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(t => t !== tag) || []
    })
  }

  const validateStep = (step: number): boolean => {
    const newErrors: string[] = []
    
    if (step === 1) {
      if (!formData.name || formData.name.length < 3) {
        newErrors.push('Strategy name must be at least 3 characters')
      }
      if (!formData.description || formData.description.length < 20) {
        newErrors.push('Description must be at least 20 characters')
      }
    }
    
    if (step === 2) {
      if (!formData.indicators || formData.indicators.length === 0) {
        newErrors.push('At least one indicator is required')
      }
    }
    
    if (step === 3) {
      if ((formData.pricing === 'paid' || formData.pricing === 'subscription') && (!formData.price || formData.price <= 0)) {
        newErrors.push('Price must be greater than 0 for paid strategies')
      }
    }
    
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4))
    }
  }

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handlePublish = async () => {
    setLoading(true)
    setErrors([])
    
    try {
      let strategyId = editDraft?.strategy_id
      
      if (editDraft) {
        // Update existing draft
        const updatePayload: UpdateDraftPayload = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          timeframe: formData.timeframe,
          indicators: formData.indicators,
          pricing: formData.pricing,
          price: formData.price,
          tags: formData.tags,
        }
        const updateRes = await updateDraft(editDraft.strategy_id, updatePayload)
        if (!updateRes.success) {
          setErrors([updateRes.error || 'Failed to update draft'])
          setLoading(false)
          return
        }
      } else {
        // Create new draft
        const createRes = await createDraft(formData)
        if (!createRes.success || !createRes.data) {
          setErrors([createRes.error || 'Failed to create draft'])
          setLoading(false)
          return
        }
        strategyId = (createRes.data as any).strategy_id
      }
      
      // Validate
      const validateRes = await validateDraft(strategyId!)
      if (!validateRes.success) {
        setErrors([validateRes.error || 'Validation failed'])
        setLoading(false)
        return
      }
      
      const validation = validateRes.data as { is_valid: boolean; errors: string[] }
      if (!validation.is_valid) {
        setErrors(validation.errors)
        setLoading(false)
        return
      }
      
      // Submit
      const submitRes = await submitDraft(strategyId!)
      if (!submitRes.success) {
        setErrors([submitRes.error || 'Failed to publish'])
        setLoading(false)
        return
      }
      
      // Success
      onSuccess?.(strategyId!)
      onClose()
      navigate('/marketplace')
      
    } catch (err) {
      setErrors(['An unexpected error occurred'])
    } finally {
      setLoading(false)
    }
  }

  const getPricingColor = () => {
    const model = PRICING_MODELS.find(p => p.value === formData.pricing)
    return model?.color || '#22c55e'
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">
              {editDraft ? '✏️ Edit Strategy' : '🚀 Publish Strategy'}
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Steps */}
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center gap-2 ${
                  currentStep >= step.id ? 'text-primary' : 'text-gray-500'
                }`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    currentStep >= step.id 
                      ? 'bg-primary text-white' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {step.icon}
                  </span>
                  <span className="hidden sm:block text-sm font-medium">{step.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`w-8 sm:w-16 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-primary' : 'bg-gray-700'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Errors */}
          {errors.length > 0 && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              {errors.map((err, i) => (
                <p key={i} className="text-red-400 text-sm">• {err}</p>
              ))}
            </div>
          )}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">📝 Basic Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Strategy Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., RSI Mean Reversion Strategy"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none"
                  maxLength={100}
                />
                <p className="text-xs text-gray-400 mt-1">{formData.name.length}/100 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your strategy, its approach, and ideal market conditions..."
                  rows={4}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none resize-none"
                  maxLength={2000}
                />
                <p className="text-xs text-gray-400 mt-1">{formData.description.length}/2000 characters (min 20)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Timeframe</label>
                <select
                  value={formData.timeframe}
                  onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none"
                >
                  {TIMEFRAMES.map(tf => (
                    <option key={tf.value} value={tf.value}>{tf.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 2: Strategy Config */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">⚙️ Strategy Configuration</h3>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Indicators <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={indicatorInput}
                    onChange={(e) => setIndicatorInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIndicator())}
                    placeholder="e.g., RSI, MACD, Bollinger Bands"
                    className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none"
                  />
                  <button
                    onClick={handleAddIndicator}
                    className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.indicators?.map(ind => (
                    <span key={ind} className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm flex items-center gap-1">
                      {ind}
                      <button 
                        onClick={() => handleRemoveIndicator(ind)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="e.g., nifty, options, intraday"
                    className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags?.map(tag => (
                    <span key={tag} className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm flex items-center gap-1">
                      {tag}
                      <button 
                        onClick={() => handleRemoveTag(tag)}
                        className="text-purple-400 hover:text-red-400"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Exchanges</label>
                <div className="flex flex-wrap gap-2">
                  {['NSE', 'NFO', 'BSE', 'MCX'].map(ex => (
                    <label key={ex} className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.exchanges?.includes(ex)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, exchanges: [...(formData.exchanges || []), ex] })
                          } else {
                            setFormData({ ...formData, exchanges: formData.exchanges?.filter(x => x !== ex) || [] })
                          }
                        }}
                        className="rounded text-primary"
                      />
                      <span className="text-white">{ex}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Instruments</label>
                <input
                  type="text"
                  value={formData.instruments?.join(', ')}
                  onChange={(e) => setFormData({
                    ...formData, 
                    instruments: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="NIFTY, BANKNIFTY, RELIANCE"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none"
                />
                <p className="text-xs text-gray-400 mt-1">Comma-separated list</p>
              </div>
            </div>
          )}

          {/* Step 3: Pricing */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">💰 Pricing Model</h3>

              <div className="grid grid-cols-2 gap-3">
                {PRICING_MODELS.map(model => (
                  <button
                    key={model.value}
                    onClick={() => setFormData({ ...formData, pricing: model.value })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      formData.pricing === model.value
                        ? 'border-primary bg-primary/10'
                        : 'border-gray-700 bg-gray-700/50 hover:border-gray-600'
                    }`}
                  >
                    <div 
                      className="w-4 h-4 rounded-full mb-2"
                      style={{ backgroundColor: model.color }}
                    />
                    <div className="font-semibold text-white">{model.label}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {model.value === 'free' && 'Free for everyone'}
                      {model.value === 'paid' && 'One-time payment'}
                      {model.value === 'subscription' && 'Monthly fee'}
                      {model.value === 'freemium' && 'Basic free, premium paid'}
                    </div>
                  </button>
                ))}
              </div>

              {(formData.pricing === 'paid' || formData.pricing === 'subscription') && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Price (₹) <span className="text-red-400">*</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl text-gray-400">₹</span>
                    <input
                      type="number"
                      value={formData.price || ''}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      min="1"
                      step="1"
                      className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-primary outline-none text-2xl font-bold"
                    />
                    {formData.pricing === 'subscription' && (
                      <span className="text-gray-400">/month</span>
                    )}
                  </div>
                  {formData.pricing === 'paid' && (
                    <p className="text-xs text-gray-400 mt-1">You receive 70% after platform fees</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">✅ Review & Publish</h3>

              <div className="bg-gray-700/50 rounded-xl p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm text-gray-400">Strategy Name</div>
                    <div className="font-semibold text-white">{formData.name}</div>
                  </div>
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded-lg"
                    style={{ backgroundColor: `${getPricingColor()}20`, color: getPricingColor() }}
                  >
                    {formData.pricing === 'free' ? 'Free' : `₹${formData.price}${formData.pricing === 'subscription' ? '/mo' : ''}`}
                  </span>
                </div>

                <div>
                  <div className="text-sm text-gray-400">Description</div>
                  <div className="text-white">{formData.description}</div>
                </div>

                <div className="flex gap-4">
                  <div>
                    <div className="text-sm text-gray-400">Category</div>
                    <div className="text-white">{CATEGORIES.find(c => c.value === formData.category)?.label}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Timeframe</div>
                    <div className="text-white">{TIMEFRAMES.find(t => t.value === formData.timeframe)?.label}</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400">Indicators</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {formData.indicators?.map(ind => (
                      <span key={ind} className="px-2 py-0.5 bg-gray-600 text-gray-300 rounded text-xs">
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>

                {formData.tags && formData.tags.length > 0 && (
                  <div>
                    <div className="text-sm text-gray-400">Tags</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 text-sm">
                  ⚠️ Once published, your strategy will be visible to all users on the marketplace.
                  Make sure all information is accurate.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-between">
          <button
            onClick={currentStep === 1 ? onClose : handleBack}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            {currentStep === 1 ? 'Cancel' : '← Back'}
          </button>
          
          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={loading}
              className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Publishing...' : '🚀 Publish Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
